import type { DialogProps } from "../misc/DialogProps";
import { Alert, Box, Dialog, DialogContent, Typography } from "@mui/material";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { AccountBalance, DrivingSchoolAccounts } from "../details/student/AccountBalance";
import { grants } from "../backoffice.access_control";
import { useRecordContext } from "react-admin";
import { Student } from "../providers/studentsProvider";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { GiftMoneyButton } from "../buttons/GiftMoneyButton";
import { MonetaryFlowEnum } from "../generated/backendClient";
import { backendUserProvider } from "../providers/backendUserProvider";

export function AppliedCreditsDialog({
  accounts,
  monetaryFlow,
  ...dialogProps
}: { accounts?: DrivingSchoolAccounts; monetaryFlow?: MonetaryFlowEnum } & DialogProps) {
  const student = useRecordContext<Student>();
  const [{ drivingSchoolId }] = useAutovioContext();

  const handleRevokeContingent = async (contingentId: string) => {
    await runSafely(() => backendUserProvider.revokeCreditsContingent(student.id, contingentId), {
      successMessage: "Guthaben wurde zurückgenommen",
      errorMessage: "Fehler beim Zurücknehmen des Guthabens",
    });
  };

  return (
    <Dialog {...dialogProps}>
      <DialogCloseButton onClick={dialogProps.onClose} />
      <DialogContent sx={{ width: 500, height: 680, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flexGrow: 1, marginTop: "60px" }}>
          <Alert severity="warning" sx={{ alignItems: "center" }}>
            <Typography variant="body2">
              Wichtig: Geschenktes Guthaben kann nur auf zukünftige Rechnungen angewendet werden. Verschenkst du
              Guthaben, wird dieses nicht auf bestehende Rechnungen angewendet.
            </Typography>
          </Alert>
          {monetaryFlow === "TRANSFER" ? (
            <AccountBalance
              key="contingents"
              title="Verschenktes Guthaben"
              accountInfo={accounts?.credits_contingents}
              onRevokeContingent={handleRevokeContingent}
            />
          ) : (
            <>
              <AccountBalance
                key="freecredits"
                title="Verschenktes Guthaben"
                accountInfo={accounts?.free_credits}
                showAmount={false}
              />
              <AccountBalance
                key="startcredits"
                title="Startguthaben"
                accountInfo={accounts?.start_credits}
                showAmount={false}
              />
              <AccountBalance key="balance" title="Saldo" accountInfo={accounts?.balance} showAmount={false} />
            </>
          )}
        </Box>
        {grants.has("giftCredits:add") && drivingSchoolId && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <GiftMoneyButton drivingSchoolId={drivingSchoolId} studentId={student.id} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
